<template>
    <div :class="{'product_row-container': true, 'free-item': props.freeItem}">
        <div class="products_row-item">
            <div class="product_row-details">
                <img :src="listItem.item.item_picture" :srcset="listItem.item.item_picture_srcset" :sizes="listItem.item.item_picture_sizes"
                    :alt="listItem.item.item_name"
                    class="products_row-card_image product_row-image" @click="visitProduct()"/>
                <div class="product_content_container">
                    <div class="product_row-description">
                        <h2 class="product_row-title title-s" @click="visitProduct()">{{ listItem.item.item_name }}</h2>
                        <p class="product_row-id paragraph-details">#{{ listItem.item_no }}</p>
                        <p class="product_row-size paragraph-details">
                            {{listItem.item.formattedVariants}}
                        </p>
                        <p v-if="props.freeItem && shouldShowBuystackButton" class="paragraph-details free-item-click-to-choose" style="cursor: pointer;" @click="openBuyStackFlyout">
                            <b>Click to choose your {{ freeItemSpecificationGroupNames.join("/") }}</b>
                        </p>

                        <div class="paragraph-details products_row-price-free" v-if="props.freeItem && canSeePricing">FREE</div>

                        <div class="paragraph-details products_row-price" v-else-if="isB2B && canSeePricing">
                            <div class="product_row-price-b2b-total">{{ listItem.formattedPrice }}</div>
                        </div>

                        <div class="paragraph-details products_row-price discount" v-else-if="canSeePricing">
                            <div class="product_row-price-total">{{ listItem.formattedPrice }}</div>
                            <div class="product_row-price-discount" v-if="listItem.formattedOrigPrice != listItem.formattedPrice">{{listItem.formattedOrigPrice}}</div>
                            <div class="product_row-price-promo_text paragraph-caption" v-if="listItem.promoMsg">{{listItem.promoMsg}}</div>
                        </div>
                    </div>
                    <div class="actions-container">
                        <div class="product_row-actions">
                            <div class="products_row-quantity">
                                <input type="number"
                                    :key="quantityInputKey" 
                                    :class="{'products_row-quantity_field small': true, 'products_row-quantity_field--disabled': props.disableEditQuantity, 'invalid-state-input': !validateQuantity(listItem.quantity)}" 
                                    :value="listItem.quantity" 
                                    @input="($event) => {
                                        listItem.original_quantity = listItem.original_quantity ?? listItem.quantity
                                        listItem.quantity = $event.target.value
                                    }"
                                    :aria-label="`Quantity for ${listItem.item.item_name }`" 
                                    :min="1"
                                    :max="999"
                                    @wheel.prevent="blurOnWheel"
                                    @blur="handleQuantityInput(listItem, $event)"
                                    :readonly="readonly"
                                />
                                <PencilIcon
                                    v-if="shouldShowBuystackButton && !props.disableEditQuantity"
                                    id="edit-button" 
                                    style="cursor: pointer; margin-left: 2px;" 
                                    :alt="`Edit ${listItem.item.item_name}`" 
                                    @click="openBuyStackFlyout" 
                                />
                                <TrashIcon
                                    :alt="`Remove ${listItem.item.item_name} from cart`" 
                                    v-if="!props.disableDeleteItem && !readonly"
                                    @click="openItemDelete()"
                                />
                            </div>
                            <div class="product_row-favorite" v-if="!props.freeItem">
                                <input type="checkbox" id="pants" class="product_row-favorite-option" :aria-label="`Save ${listItem.item.item_name}`" />
                                <label for="pants" class="product_row-favorite_label"  :class="{'cursor-pointer': !readonly }" @click="toggleFavorite">
                                    <FavoriteIconCartEmpty v-if="!isFavorite" :alt="`Mark ${listItem.item.item_name} as favorite`" />
                                    <FavoriteIconCart v-else :alt="`${listItem.item.item_name} marked as favorite`"/>
                                    <p class="paragraph-details favorites-cta-text">{{ isFavorite ? 'Remove from ' : 'Save to '  }}Favorites</p>
                                </label>
                            </div>
                        </div>
                        <div v-if="canSeePricing && props.freeItem" class="product_row-total_price title-s mobile">
                            <div class="paragraph-details products_row-price-free">FREE</div>
                        </div>
                        <div v-else-if="canSeePricing" class="product_row-total_price title-s mobile">{{ listItem.formattedSubtotal }}</div>
                    </div>
                </div>
            </div>
            <div v-if="canSeePricing && props.freeItem" class="product_row-total_price title-s desktop">
                <div class="paragraph-details products_row-price-free">FREE</div>
            </div>
            <div v-else-if="canSeePricing" class="product_row-total_price title-s desktop">{{ listItem.formattedSubtotal }}</div>
        </div>
        <p class="input-support-text invalid-state paragraph-details" v-if="props.isRestricted">This product is under a restriction. Please remove from cart to continue.</p>
        <div class="notification notification-error" v-if="outOfStock">
            <ShoppingCartError alt="Cart error icon" />
            <div class="product_row-error_text">
                <div class="paragraph-details text-rothcoWhite bold">This product does not currently have enough
                            inventory to complete your
                            order.</div>
                <div class="paragraph-details text-rothcoWhite" v-if="isB2B">You can update the quantity to {{listItem.item.inventory_level}} or less, remove the item, or
                        proceed to checkout as quote.</div>
                <div class="paragraph-details text-rothcoWhite" v-else>You can update the quantity, remove the item, or
                        save to favorites.</div>
            </div>
        </div>
    </div>
</template>
<script lang="ts" setup>
    import { computed, ref, watch, inject, onMounted } from "vue";
    import { useLists } from "@/helpers/composables/useList";
    import { useCartDetails } from "@/helpers/composables/useCartDetails";
    import ListDeleteModal from "@/components/ListDeleteModal.vue";
    import { router } from "@inertiajs/vue3";
    // import { route } from "ziggy-js";
    import { createUseFavoritesStore } from "@/store/favorites";
    import { useFavoritesStore } from "@/store/favorites";
    import { useSettingsStore } from "@/store/settings";
    import { storeToRefs } from "pinia";
    import { useDialog } from 'primevue/usedialog';
    import ShoppingCartError from "@icons/carbon_shopping-cart-error.svg";
    import FavoriteIconCartEmpty from "@icons/favorite-icon-cart-empty.svg";
    import FavoriteIconCart from "@icons/favorite-icon-cart.svg";
    import TrashIcon from "@icons/trash-icon.svg";
    import PencilIcon from "@icons/pencil.svg";
    import useGoogleManager from "@/helpers/composables/google";
    import { useStorage } from '@vueuse/core';
    import { useCheckoutD2CStore } from '@/store/checkoutD2CStore';
    import {
        getPseudoQtyForItem,
        quantityForBackendWithPseudoLine,
    } from "@/helpers/composables/useCouponPseudoLine";
    const quantityInputKey = ref(0);

    const dialog = useDialog();

    const couponCode = useStorage("coupon", null);
    
    let props = defineProps({
        list_item: {
            type: Object<any>,
            default: {}
        },
        isRestricted: {
            type: Boolean,
            default: false
        },
        readonly: {
            type: Boolean,
            default: false
        },
        freeItem: {
            type: Boolean,
            default: false
        },
        disableEditQuantity: {
            type: Boolean,
            default: false
        },
        disableDeleteItem: {
            type: Boolean,
            default: false
        },
        only: {
            type: Array<string>(),
            default: () => []
        }
    });
    let route = inject("route");

    const list_item = computed(()=>{
        return props.list_item;
    });

    const outOfStock = computed(() => {
        return listItem.value.item.inventory_level < listItem.value.quantity;
    });

    let { listItem } = useCartDetails(list_item);
    const readonly = computed(()=>{
        return props.readonly || props.disableEditQuantity;
    });

    const { reapplyCoupon } = useCheckoutD2CStore();
    const {
        couponInformation
    } = storeToRefs(useCheckoutD2CStore());

    const shouldShowBuystackButton = computed(() => {
        if (!!props.freeItem) {
            const variationIds =
                couponInformation.value?.freeItemVariations?.ids ?? [];
            return variationIds.length > 1;
        }

        return true;
    });

    const freeItemSpecificationGroupNames = computed(() => {
        return couponInformation.value?.freeItemVariations?.specificationGroupNames ?? ['Style'];
    });

    const pseudoQty = computed(() =>
        getPseudoQtyForItem(
            couponInformation.value?.items,
            props.list_item?.item_variation_id
        )
    );

    const visitProduct = () => {
        if(listItem.value?.item?.seo_name){
            router.visit(route('productDetail.show', [listItem.value.item.seo_name]), {
                method: 'get'
            })
        }
    }

    let openBuyStackFlyout = function () {
        if (!shouldShowBuystackButton.value) {
            visitProduct();
            return;
        }
        toggleBuystackFlyout(
            {
                seo_name: props.list_item.variation.item.seo_name,
                rothco_item_no: props.list_item.variation.rothcoItem.rothco_item_no,
                item_index: props.list_item.variation.item.item_index,
                disable_quantity_input: props.disableEditQuantity,
                open_details_to_new_tab: false,
                item_list_info: {
                    list_id: props.list_item.list_id, 
                    list_item_id: props.list_item.list_item_id, 
                    quantity: props.list_item.quantity,
                },
                coupon_info: props.freeItem
                    ? {
                          freeItemVariationIds:
                              couponInformation.value?.freeItemVariations?.ids ?? [],
                      }
                    : null,
                onListItemUpdate: (updates) => {
                    if(updates?.quantity != null){
                        listItem.value.quantity = updates.quantity;
                    }
                    if (updates?.item_variation_id != null) {
                        listItem.value.item_variation_id = updates.item_variation_id;
                    }
                    if(props.list_item?.list_item_id){
                        updateItem(listItem.value);
                    }else{
                        updateCouponItemSelection({
                            list_id: props.list_item.list_id,
                            item_variation_id: updates?.item_variation_id
                        });
                    }
                }
            }
        );
    };

    let { updateListItem, updateCouponItem } = useLists({ router, route });

    const emit = defineEmits(["updateItemQuantity", "getTax"]);
    let { gtmRemoveItemFromCartEvent } = useGoogleManager();
    let openItemDelete = () => {
        dialog.open(ListDeleteModal, {
            props: {
                showHeader: false,
                modal: true
            },
            data: {
                type:"item",
                list_id:'',
                list_item_id:listItem.value.list_item_id,
                itemNumber: props.list_item.variation.item.product_line_code,
                beforeCloseForItem(ctx) {
                    return new Promise((resolve) => {
                        gtmRemoveItemFromCartEvent({
                            item: {
                                name: listItem.value.item.item_name,
                            },
                            rothcoItem: {
                                rothco_item_no: listItem.value.item_no,
                            },
                            item_variation_id: props.list_item.item_variation_id,
                            quantity: listItem.value.quantity,
                            price: props.list_item.price,
                        })
                        reapplyCoupon();
                        router.reload();
                        resolve(ctx);
                    });
                }
            },
            onClose: (props) => {
                if(props?.data?.cancelled){
                    listItem.value.quantity = listItem.value.original_quantity;
                    quantityInputKey.value++;
                }else{
                }
            }
        });
    };

    function validateQuantity(qty) {
        return !Number.isNaN(+qty) && +qty >= 0 && +qty % 1 == 0;
    }

    let handleQuantityInput = function (row, event: Event) {
        let oldVal = row.original_quantity ?? row.quantity;
        let newVal: number | null = listItem.value.quantity;
        if(newVal == 0) {
            return openItemDelete();
        }
        
        if(!validateQuantity(newVal)) return
        
        newVal = newVal ? parseInt(newVal.toString()) : null;

        if (newVal != oldVal) {
            emit("updateItemQuantity", listItem);
            updateItem(listItem.value);
        }
    };

    const updateItem = function (listItem) {
        const payload = {
            ...listItem,
            quantity: quantityForBackendWithPseudoLine(listItem.quantity, pseudoQty.value),
        };
        updateListItem(payload, {
            afterFetch: () => {
                emit('getTax');
                reapplyCoupon();
                return new Promise((resolve) => {
                    router.reload({
                        only: props.only,
                        onSuccess: (data) => {
                            resolve(data);
                        }
                    });
                });
            }
        });
    };

    const updateCouponItemSelection = function (listItem) {
        updateCouponItem({
            coupon: couponCode.value,
            list_id: listItem.list_id,
            item_variation_id: listItem.item_variation_id,
        }, {
            afterFetch: () => {
                emit('getTax');
                reapplyCoupon();
                return new Promise((resolve) => {
                    router.reload({
                        only: props.only,
                        onSuccess: (data) => {
                            resolve(data);
                        }
                    });
                });
            }
        });
    };

    let settings = useSettingsStore();
    let { toggleFlyout, toggleBuystackFlyout } = settings;
    let { user, isLoggedIn, canSeePricing } = storeToRefs(settings);

    let { getFavorites } = useFavoritesStore();

    let favoritesStore = createUseFavoritesStore(`favorite-${list_item.value.variation.item.item_index}`, {
        item_index: list_item.value.variation.item.item_index,
        user_id: user.value.user_id
    })();

    let isB2B = computed(() => user.value.is_dealer);

    let { favoriteId } = storeToRefs(favoritesStore);
    let { addFavorite, deleteFavorite } = favoritesStore;

    let isFavorite = computed(()=>{
        return favoriteId?.value > 0;
    });

    const handleAccountLogin = () => {
        toggleFlyout();
    };

    const toggleFavorite = function () {
        if(!isLoggedIn.value) {
            handleAccountLogin();
            return;
        }

        if (readonly.value) return;
        if (isFavorite.value) {
            deleteFavorite();
        } else {
            addFavorite();
        }
    };

    watch(outOfStock, (newVal) => {
      emit('listItemError', { index: listItem.value.list_item_id, outOfStock: newVal });
    });

    onMounted(()=>{
        if(!isLoggedIn.value) {
            return;
        }

        getFavorites();
    })

    function blurOnWheel(event) {
        event.target.blur();
    }
</script>
